/**
 * Group-thread READ routing flag (group-threading P3c, Option B).
 *
 * Gates the client read of the shared `group_thread_messages/{threadId}` store
 * (the overlay merged into thread-open reads, and the P3d sender attribution).
 *
 * ACCOUNT-SCOPED, matching the sibling group-threading flags
 * (threadAddPeople / threadMentions / multi-choice panel). PLATFORM DEFAULT ON
 * (owner directive 2026-08-17, completing the all-accounts rollout approved
 * 2026-07-24): `GROUP_THREAD_READ_ENABLED` is consulted AFTER the blank-account
 * guard — it acts like a '*' allowlist token, never a bypass, so anonymous /
 * not-yet-resolved sessions stay fail-closed. Rollback: flip the constant to
 * false and the `REACT_APP_GROUP_THREAD_READ_ACCOUNTS` CSV governs again.
 *
 * The overlay read happens deep in the repository layer, which has no accountId
 * in scope, so the active account is published once by the chat mount via
 * `setGroupThreadReadAccount` and used as the default when a caller doesn't pass
 * one. The render path (which has the accountId) passes it explicitly.
 *
 * Even with the flag on, the real data containment is server-side: firestore
 * rules only admit thread PARTICIPANTS, so a non-participant read is denied and
 * swallowed regardless of any client flag state.
 */
export const GROUP_THREAD_READ_ENABLED: boolean = true;

export const GROUP_THREAD_READ_ACCOUNTS_ENV = 'REACT_APP_GROUP_THREAD_READ_ACCOUNTS';

// Active account published by the chat mount for the repository-layer reads.
let activeAccountId: string | null = null;
export function setGroupThreadReadAccount(accountId: string | null | undefined): void {
  activeAccountId = (accountId ?? '').trim() || null;
}

function readEnabledReadAccountsFromEnv(env?: Record<string, string | undefined>): Set<string> {
  const raw = (env
    ? env[GROUP_THREAD_READ_ACCOUNTS_ENV]
    : process.env.REACT_APP_GROUP_THREAD_READ_ACCOUNTS) ?? '';
  return new Set(
    String(raw)
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean),
  );
}

export interface GroupThreadReadFlagCtx {
  /** Account to gate on; defaults to the mount-published active account. */
  accountId?: string | null;
  /** Test/override seam: explicit allowlist instead of the env CSV. */
  enabledAccountIds?: Iterable<string>;
  /** Test seam: injected env map. Production uses the inlined literal. */
  env?: Record<string, string | undefined>;
  /** Test seam for the compile default (rollback-path tests). Production callers never pass this. */
  defaultEnabled?: boolean;
}

export function resolveGroupThreadReadEnabled(ctx: GroupThreadReadFlagCtx = {}): boolean {
  const accountId = (ctx.accountId ?? activeAccountId ?? '').trim();
  if (!accountId) return false;
  // Compile default AFTER the blank guard — the platform default can never
  // admit an unresolved session (unlike the pre-2026-08-17 early return).
  if (ctx.defaultEnabled ?? GROUP_THREAD_READ_ENABLED) return true;
  const source = ctx.enabledAccountIds ?? readEnabledReadAccountsFromEnv(ctx.env);
  const set = source instanceof Set ? source : new Set(source);
  // ALL-ACCOUNTS ROLLOUT (approved 2026-07-24): the literal '*' in the CSV
  // admits every RESOLVED account. Deliberately checked AFTER the blank-account
  // fail-closed guard above — unlike the compile-constant override, '*' never
  // enables group reads for anonymous / not-yet-resolved sessions. Rollback is
  // a repo-variable edit + frontend redeploy, no code revert.
  if (set.has('*')) return true;
  return set.has(accountId);
}
